/**
 * URL helpers for preserving side-panel state across navigation.
 *
 * The chat side panel opens when the URL carries BOTH `?chat=<taskId>` AND
 * `?agent=<taskClassName>` (see tab-content-area.tsx:45 — `agent` is the
 * fallback when the destination page has no active-tab metadata for the
 * task). Any intra-app link that should keep the chat panel visible needs
 * both params preserved. Call sites:
 *
 *     const params = useChatUrlParams();
 *     const href = appendChatParams('/requirements/abc', params);
 *
 * External URLs pass through unchanged.
 */

'use client';

import { createContext, useContext } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

export interface ChatUrlParams {
  chatId: string | null;
  agent: string | null;
}

/**
 * Context ChatView publishes so deep descendants (link-rendering components
 * inside messages) can append the right `chat` + `agent` params without
 * re-deriving them. Falls back to URL detection when absent.
 */
export const ChatNavContext = createContext<ChatUrlParams | null>(null);

/**
 * Append `?chat=<chatId>&agent=<agent>` to `href` for intra-app links.
 * No-op for external URLs, when `chatId` is absent, or when the href
 * already carries a `chat=` query param (idempotent — avoids
 * double-appending when `buildUrl` already preserved from
 * `window.location.search`).
 */
export function appendChatParams(
  href: string,
  params: ChatUrlParams,
): string {
  const { chatId, agent } = params;
  if (!chatId) return href;
  // External URLs (http://, https://, //) are left alone — don't leak
  // internal ids and don't break absolute hrefs.
  if (/^(?:[a-z]+:)?\/\//i.test(href)) return href;
  const qIdx = href.indexOf('?');
  // Merge into any params already on `href` rather than blindly
  // appending. `buildUrl` may already have preserved `agent` (from
  // window.location.search) WITHOUT `chat` — appending a fresh
  // `agent` here would duplicate it (`?agent=X&chat=Y&agent=X`).
  const existing = new URLSearchParams(qIdx >= 0 ? href.slice(qIdx + 1) : '');
  if (existing.has('chat')) return href;
  existing.set('chat', chatId);
  if (agent && !existing.has('agent')) existing.set('agent', agent);
  const base = qIdx >= 0 ? href.slice(0, qIdx) : href;
  return `${base}?${existing.toString()}`;
}

/**
 * Hook: returns the current chat+agent params.
 *
 * Prefers `ChatNavContext` (set by `ChatView` — always correct when called
 * from inside a chat message), then falls back to URL inspection:
 *   - `?chat=<id>` + `?agent=<class>` query params
 *   - `/chat/:taskId` route param (mirrors tab-content-area.tsx:32-42)
 *
 * The URL fallback handles the case where a chat panel is open on a route
 * that has `chat` in the path rather than the query, and ensures links
 * emitted from the chat always carry the chat context forward.
 */
export function useChatUrlParams(): ChatUrlParams {
  const ctx = useContext(ChatNavContext);
  const searchParams = useSearchParams();
  const pathname = usePathname();

  if (ctx && ctx.chatId) return ctx;

  let chatId = searchParams.get('chat');
  if (!chatId && pathname?.includes('/chat/')) {
    const match = pathname.match(/\/chat\/([^\/]+)/);
    if (match && match[1] !== 'new') {
      chatId = match[1];
    }
  }
  return {
    chatId,
    agent: searchParams.get('agent'),
  };
}
